/**
 * OtpInput — segmented numeric code entry, one input per digit.
 * Web counterpart of the mobile `react-native-otp-entry` field.
 */
import { useRef, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';

interface OtpInputProps {
  value: string;
  length: number;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
}

const DIGITS_ONLY = /\D/g;

export function OtpInput({
  value,
  length,
  onChange,
  onComplete,
  disabled = false,
  invalid = false,
  describedBy,
}: OtpInputProps) {
  const { t } = useTranslation();
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const focusIndex = (index: number) => {
    inputsRef.current[Math.min(Math.max(index, 0), length - 1)]?.focus();
  };

  // Strip any non-digit padding so spaces never reach the API. The value
  // may contain spaces when a user types in a later box first (padEnd below);
  // commit cleans them so onChange/onComplete always receive digits only.
  const commit = (next: string) => {
    const cleaned = next.replace(DIGITS_ONLY, '');
    onChange(cleaned);
    if (cleaned.length === length) {
      onComplete?.(cleaned);
    }
  };

  const handleChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const typed = event.target.value.replace(DIGITS_ONLY, '');
    if (!typed) {
      return;
    }
    const chars = value.padEnd(length, ' ').split('');
    typed.split('').forEach((char, offset) => {
      if (index + offset < length) {
        chars[index + offset] = char;
      }
    });
    commit(chars.join(''));
    focusIndex(index + typed.length);
  };

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const chars = value.split('');
      if (chars[index]) {
        chars[index] = '';
      } else {
        chars[index - 1] = '';
        focusIndex(index - 1);
      }
      commit(chars.join(''));
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusIndex(index - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusIndex(index + 1);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').replace(DIGITS_ONLY, '').slice(0, length);
    if (!pasted) {
      return;
    }
    event.preventDefault();
    commit(pasted);
    focusIndex(pasted.length);
  };

  return (
    <div className="otp-input" role="group" aria-label={t('auth.verificationCode.codeLabel')}>
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={element => {
            inputsRef.current[index] = element;
          }}
          className={`otp-input__box${invalid ? ' otp-input__box--error' : ''}`}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={value[index] ?? ''}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste}
          onFocus={event => event.target.select()}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          aria-label={t('auth.verificationCode.digitLabel', { index: index + 1, total: length })}
          autoFocus={index === 0}
        />
      ))}
    </div>
  );
}
